import { gql } from '@apollo/client';
import { useFeatureToggle } from '@flopflip/react-broadcast';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { forwardRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { flagNames } from '../../../featureFlags';
import { userIsClient } from '../../../services/Utilities';
import ListViewItem from '../../standard/ListViewItem';
import DeleteMatterModal from '../DeleteMatterModal';
import MatterStateBadge from '../view/MatterStateBadge';

const IN_PROGRESS_STATES = ['qSent', 'qInProgress'];

export const MatterViewFragment = gql`
  fragment MatterView on Matter {
    matterId: _id
    name
    state
    updatedAt
    createdAt
    canRead
    sharedWithClient
    legacyQuestionnaireState
    matterVersion
    owner
    organization {
      _id
      name
    }
    signatureRequests {
      id
      requesterEmailAddress
      createdAt
      detailsUrl
      title
      status
      signatures {
        id
        name
        email
        status
        signedAt
        lastViewedAt
        lastRemindedAt
      }
    }
  }
`;

function ClientMatterDescription({
  updatedAt,
  createdAt,
  sharedWithClient,
  state,
}) {
  const dateString =
    updatedAt !== createdAt
      ? `Last Modified ${new Date(updatedAt).toLocaleString('en-US')}`
      : `Created on ${new Date(createdAt).toLocaleString('en-US')}`;

  if (!sharedWithClient) {
    return state === 'open'
      ? 'This matter is being reviewed by your service provider.'
      : 'This matter has not been shared by your service provider.';
  }
  return dateString;
}

function FirmMatterDescription({
  updatedAt,
  createdAt,
  sharedWithClient,
  organization,
}) {
  const { name } = organization ?? {};
  const dateString =
    updatedAt !== createdAt
      ? `Last Modified ${new Date(updatedAt).toLocaleString('en-US')}`
      : `Created on ${new Date(createdAt).toLocaleString('en-US')}`;

  return (
    <>
      {!!name && <>{`${name} | `}</>}
      {sharedWithClient ? (
        <>
          <FontAwesomeIcon icon={faEye} /> {' Shared | '}
        </>
      ) : (
        <>
          <FontAwesomeIcon icon={faEyeSlash} /> {'Not Shared | '}
        </>
      )}
      {dateString}
    </>
  );
}

export const MatterListViewItem = forwardRef(
  (
    {
      matter,
      disableMenu = false,
      menuItems = [],
      onDelete,
      signatureStatus,
      ...props
    },
    ref,
  ) => {
    const userType = localStorage.getItem('userType');
    const userId = localStorage.getItem('userId');

    const history = useHistory();

    const [matterToDelete, setMatterToDelete] = useState(null);

    const enableClientMatterManagement = useFeatureToggle(
      flagNames.ENABLE_CLIENT_MATTER_MANAGEMENT,
    );

    const {
      canRead,
      createdAt,
      legacyQuestionnaireState,
      matterId,
      matterVersion,
      name,
      sharedWithClient,
      state,
      updatedAt,
      organization,
      owner,
    } = matter;

    const description =
      userType === 'client' ? (
        <ClientMatterDescription
          updatedAt={updatedAt}
          createdAt={createdAt}
          sharedWithClient={sharedWithClient}
          state={state}
        />
      ) : (
        <FirmMatterDescription
          updatedAt={updatedAt}
          createdAt={createdAt}
          sharedWithClient={sharedWithClient}
          organization={organization}
        />
      );

    const onClick = useCallback(() => {
      if (userIsClient()) {
        history.push(
          `/client/${matterVersion === 1 ? 'matter' : 'matters'}/${matterId}`,
        );
      } else {
        const clientPath = organization ? `/clients/${organization._id}` : '';
        const matterPath = matterVersion === 1 ? 'contracts' : 'matters';
        const matterSuffix = IN_PROGRESS_STATES.includes(
          legacyQuestionnaireState,
        )
          ? '/edit'
          : '';
        history.push(`${clientPath}/${matterPath}/${matterId}${matterSuffix}`);
      }
    }, [
      matterId,
      matterVersion,
      legacyQuestionnaireState,
      organization,
      history,
    ]);

    const canDelete =
      (!userIsClient() ||
        enableClientMatterManagement ||
        owner === userId.replace(/^.*\|/, '')) &&
      canRead;

    return (
      <>
        <ListViewItem
          {...props}
          id={matterId}
          key={matterId}
          title={name}
          description={description}
          onClick={canRead ? onClick : undefined}
          isActive={!!canRead}
          maintainMenuSpace={!disableMenu}
          menuItems={
            !disableMenu &&
            [
              ...menuItems,
              signatureStatus && {
                label: 'View signature requests',
                onClick: () => {}, // TODO: add signature status card functionality
              },
              canDelete && {
                label: 'Delete',
                onClick: (e) => {
                  e.stopPropagation();
                  setMatterToDelete(matter);
                },
              },
            ].filter((menuItem) => !!menuItem)
          }
          right={
            <MatterStateBadge
              collapseOnMobile
              state={legacyQuestionnaireState || state}
              isActive={!!canRead}
              signatureStatus={signatureStatus}
            />
          }
          ref={ref}
        />
        <DeleteMatterModal
          matterToDelete={matterToDelete}
          onClose={() => setMatterToDelete(null)}
          onDelete={onDelete}
        />
      </>
    );
  },
);
